/**
 * Path helpers for the case-setup screen.
 *
 * Host paths may be POSIX or Windows, so both separators are handled here; the
 * container side is always `/` (CONTRACT.md §4). Nothing in this module touches
 * the filesystem — it only derives what the Docker command will look like so the
 * user can see the mounts before running.
 */

const SEP = /[\\/]/;

export function baseName(path: string): string {
  const trimmed = path.replace(/[\\/]+$/, "");
  const parts = trimmed.split(SEP);
  return parts[parts.length - 1] ?? "";
}

export function parentDir(path: string): string {
  const trimmed = path.replace(/[\\/]+$/, "");
  const idx = Math.max(trimmed.lastIndexOf("/"), trimmed.lastIndexOf("\\"));
  if (idx <= 0) return trimmed;
  return trimmed.slice(0, idx);
}

/** Container paths always use `/`, even when the host path is a Windows one. */
export function toContainerRel(relPath: string): string {
  return relPath.replace(/\\/g, "/");
}

export interface MountRow {
  hostPath: string;
  containerPath: string;
  access: "Read Only" | "Read/Write";
  purpose: string;
}

/**
 * The mounts `docker run` will receive. Derived from the case draft, not user
 * editable — the Rust side builds exactly `-v <inputRoot>:/data:ro` and
 * `-v <outputRoot>:/out`, so showing anything else here would be a lie.
 *
 * The native engine has no container and therefore no mounts: it reads and
 * writes the host paths directly. Showing the Docker table in that case would
 * describe a translation that is not happening.
 */
export function deriveMounts(
  inputRoot: string,
  outputRoot: string,
  engineKind: "native" | "docker" = "docker",
): MountRow[] {
  if (engineKind === "native") {
    return [
      {
        hostPath: inputRoot || "—",
        containerPath: "(used directly)",
        access: "Read Only",
        purpose: "Subject folder(s); the pipeline never writes here.",
      },
      {
        hostPath: outputRoot || "—",
        containerPath: "(used directly)",
        access: "Read/Write",
        purpose: "Everything the run produces, under <subject id>/.",
      },
    ];
  }
  return [
    {
      hostPath: inputRoot || "—",
      containerPath: "/data",
      access: "Read Only",
      purpose: "Subject folder(s); the pipeline never writes here.",
    },
    {
      hostPath: outputRoot || "—",
      containerPath: "/out",
      access: "Read/Write",
      purpose: "Everything the run produces, under <subject id>/.",
    },
  ];
}

/**
 * The input root is the parent of the subject folder: the runner is given
 * `/data/<subjectRelPath>`, so the mount has to sit one level above.
 */
export function inputRootFor(subjectDir: string): string {
  return subjectDir ? parentDir(subjectDir) : "";
}

export function containerSubjectPath(subjectDir: string): string {
  return subjectDir ? `/data/${toContainerRel(baseName(subjectDir))}` : "—";
}

/** Truncate a `sha256:…` image id the way `docker images` does. */
export function shortImageId(imageId: string): string {
  const hex = imageId.startsWith("sha256:") ? imageId.slice(7) : imageId;
  return hex.slice(0, 12);
}
